import { Base } from '@studiometa/js-toolkit';
import { addClass, removeClass } from '@studiometa/js-toolkit/utils';
import { untrapFocus, trapFocus } from '@studiometa/js-toolkit/utils';

/**
 * Facets component
 */
export default class Search extends Base {
  timeout = null;

  /**
   * Component Configuration
   */
  static config = {
    name: 'Search',
    refs: ['form', 'input', 'close', 'container', 'loader'],
  };

  /**
   * Set Default container
   * @returns {void}
   */
  mounted() {
    this.defaultContainer = this.$refs.container.innerHTML;
  }

  /**
   * Get input value
   * @returns {string} current value.
   */
  get searchTerm() {
    return this.$refs.input.value;
  }

  /**
   * Perform search when typing something in input
   * @returns {void}
   */
  onInputInput() {
    this.$refs.loader.style.display = 'block';

    clearTimeout(this.timeout);

    if (!this.searchTerm) {
      this.$refs.loader.style.display = 'none';
      this.$refs.container.innerHTML = this.defaultContainer;
      return;
    }

    this.timeout = setTimeout(() => {
      fetch(
        `${window.routes.predictive_search_url}?q=${encodeURIComponent(this.searchTerm)}&section_id=predictive-search`,
      )
        .then((response) => {
          if (!response.ok) {
            const error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const results = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;

          this.renderResults(results);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          throw error;
        });
    }, 800);
  }

  /**
   * Set results to the page
   * @param  {HTML} results products results
   * @returns {void}
   */
  renderResults(results) {
    this.$refs.loader.style.display = 'none';
    this.$refs.container.innerHTML = results;
  }

  /**
   * Open Search
   * @returns {void}
   */
  open() {
    document.addEventListener('keyup', (event) => {
      trapFocus(this.$el, event);
    });
    addClass(document.documentElement, ['overflow-hidden', 'is-search-open']);
    removeClass(this.$el, ['opacity-0', 'pointer-events-none']);
    this.$el.setAttribute('aria-hidden', 'false');
    this.$el.removeAttribute('inert');
    this.$refs.input.focus();
  }

  /**
   * Close search
   * @returns {void}
   */
  close() {
    untrapFocus();
    removeClass(document.documentElement, ['overflow-hidden', 'is-search-open']);
    addClass(this.$el, ['opacity-0', 'pointer-events-none']);
    this.$el.setAttribute('aria-hidden', 'true');
    this.$el.setAttribute('inert', '');
    this.$refs.input.value = '';
  }

  /**
   * Click on close btn
   * @returns {void}
   */
  onCloseClick() {
    this.close();
  }

  /**
   * Close panel when type on Escape key
   * @param {object} props Props
   * @param {object} props.ESC Escape key
   * @returns {void}
   */
  keyed({ ESC }) {
    if (ESC) {
      this.close();
    }
  }
}
